import type Map from 'ol/Map';
import { getPointResolution } from 'ol/proj';
import type { AppState } from '../core/types';
import { emptySelectionSet } from '../domain/selectionSet';
import { ELEVATION_OVERLAY_ROLE } from '../map/ElevationOverlay';
import { buildPresentationComposition } from '../presentation/compositor';
import { compositeOpenLayersCanvases, drawPresentationOverlays, renderLegendCanvas } from './canvasRenderer';
import { assertCanvasExportable, CanvasExportSecurityError, expandExtentByMargin, exportPixelSize, exportWidths, extentForExport, suggestedExportFilename, type MapExportOptions } from './mapExport';

const waitForRender = (map: Map): Promise<void> => new Promise((resolve, reject) => {
  const timeout = window.setTimeout(() => reject(new Error('El mapa no terminó de renderizar la exportación a tiempo.')), 20_000);
  map.once('rendercomplete', () => { window.clearTimeout(timeout); resolve(); });
  map.renderSync();
});

const canvasBlob = (canvas: HTMLCanvasElement): Promise<Blob> => new Promise((resolve, reject) => {
  try { canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('El navegador no pudo codificar el PNG.')), 'image/png'); }
  catch (error) { reject(error); }
});

export class MapExportService {
  constructor(private readonly map: Map, private readonly renderState: (state: AppState, labels?: boolean, renderScale?: number) => void) {}

  async export(state: AppState, options: MapExportOptions): Promise<{ filename: string; width: number; height: number }> {
    const composition = buildPresentationComposition(state);
    const filename = suggestedExportFilename(state, options.content, options.scope);
    let canvas: HTMLCanvasElement;
    if (options.content === 'legend') {
      if (!composition.legendModel) throw new Error('No hay una leyenda temática visible para exportar.');
      const quality = exportWidths[options.resolution] / 960;
      canvas = renderLegendCanvas(composition.legendModel, quality * composition.legend.scale);
    } else canvas = await this.renderMap(state, composition, options);
    assertCanvasExportable(canvas);
    const blob = await canvasBlob(canvas); const url = URL.createObjectURL(blob); const link = document.createElement('a');
    link.href = url; link.download = filename; link.click(); window.setTimeout(() => URL.revokeObjectURL(url), 0);
    return { filename, width: canvas.width, height: canvas.height };
  }

  private async renderMap(state: AppState, composition: ReturnType<typeof buildPresentationComposition>, options: MapExportOptions): Promise<HTMLCanvasElement> {
    const target = this.map.getTargetElement(); const originalSize = this.map.getSize(); if (!originalSize) throw new Error('El mapa todavía no tiene un tamaño exportable.');
    const originalMapWidth = originalSize[0] ?? 1; const originalMapHeight = originalSize[1] ?? 1;
    const width = exportWidths[options.resolution]; const size = exportPixelSize(width, [originalMapWidth, originalMapHeight]);
    const view = this.map.getView(); const originalCenter = view.getCenter(); const originalResolution = view.getResolution(); const originalRotation = view.getRotation();
    const originalWidth = target.style.width; const originalHeight = target.style.height;
    const layers = this.map.getAllLayers(); const visibility = layers.map((layer) => layer.getVisible());
    const useBasemap = options.showBasemap && options.background === 'basemap' && state.basemap !== 'none';
    const exportState: AppState = { ...state, selection: null, genericSelection: null, selectionSet: emptySelectionSet(), presentationWorkspace: { ...state.presentationWorkspace, showBasemap: useBasemap } };
    const extent = extentForExport(state, options.scope, view.calculateExtent(originalSize));
    if (!extent) throw new Error(options.scope === 'selection' ? 'Selecciona al menos un objeto visible antes de exportar la selección.' : 'No hay objetos visibles para ajustar la exportación.');
    try {
      layers.forEach((layer) => {
        if (layer.get('basemapId')) layer.setVisible(useBasemap && layer.get('basemapId') === state.basemap);
        if (layer.get('role') === 'labels') layer.setVisible(options.showLabels);
        if (layer.get('role') === 'measurement' || layer.get('role') === 'partial-preview' || layer.get('role') === 'map-location' || layer.get('role') === 'planning-edit' || layer.get('role') === ELEVATION_OVERLAY_ROLE) layer.setVisible(false);
      });
      target.style.width = `${size[0]}px`; target.style.height = `${size[1]}px`; this.map.setSize(size);
      if (options.scope !== 'current-view') view.fit(expandExtentByMargin(extent, options.marginPercent), { size, duration: 0 });
      else if (originalResolution) view.setResolution(originalResolution * originalMapWidth / size[0]);
      this.renderState(exportState, options.showLabels, size[0] / originalMapWidth);
      await waitForRender(this.map);
      const canvas = compositeOpenLayersCanvases(target, size[0], size[1], options.background === 'transparent' ? 'transparent' : 'white');
      const resolution = view.getResolution() ?? 1; const center = view.getCenter() ?? [0, 0];
      const metersPerPixel = getPointResolution(view.getProjection(), resolution, center, 'm');
      drawPresentationOverlays(canvas, composition, { legend: options.content === 'map-legend' && options.showLegend, scale: options.showScale, north: options.showNorth, metersPerPixel, designScale: size[0] / originalMapWidth });
      if (useBasemap) {
        try { assertCanvasExportable(canvas); }
        catch { throw new CanvasExportSecurityError(); }
      }
      return canvas;
    } finally {
      layers.forEach((layer, index) => layer.setVisible(visibility[index] ?? false));
      target.style.width = originalWidth; target.style.height = originalHeight; this.map.setSize(originalSize);
      if (originalCenter) view.setCenter(originalCenter); if (originalResolution) view.setResolution(originalResolution); view.setRotation(originalRotation); this.renderState(state); this.map.renderSync();
    }
  }
}
